// Import React hooks and necessary modules
import { useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Functional component for uploading a profile photo
function UploadPhotoPage() {
  // Get authenticated user info and loading state from custom hook
  const { user, isLoading, refreshUser } = useAuth();
  // State for storing the selected file
  const [file, setFile] = useState<File | null>(null);
  // State for error messages
  const [error, setError] = useState<string | null>(null);
  // State for success messages
  const [success, setSuccess] = useState<string | null>(null);
  // Navigation functions
  const navigate = useNavigate();
  const location = useLocation();
  // Handle file input changes
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };
  // Handle form submission for uploading the photo
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate that a file has been selected
    if (!file) {
      setError('Please select a file');
      return;
    }
    try {
      // Fetch CSRF token for secure POST request
      const response = await axios.get('/elearning/get-csrf/', { withCredentials: true });
      const csrfToken = response.data.csrfToken;
      // Prepare form data with the selected file
      const formData = new FormData();
      formData.append('photo', file);
      // Send POST request to upload the photo
      await axios.post('/elearning/api/users/me/upload-photo/', formData, {
        withCredentials: true,
        headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'multipart/form-data' },
      });
      // Update success state and reset error
      setSuccess('Photo uploaded successfully');
      setError(null);
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
      // Refresh user data to reflect updated photo
      await refreshUser();
      // Navigate back to profile page
      navigate('/profile');
    } catch (err: any) {
      // Handle errors during upload
      console.error('UploadPhotoPage: Error uploading photo=', err);
      setError(err.response?.data?.message || 'Failed to upload photo');
      setSuccess(null);
      setTimeout(() => setError(null), 5000);
    }
  };
  // Handle back button navigation
  const handleBack = () => {
    navigate(location.state?.from || '/profile');
  };
  // Show loading spinner while user data is loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  // Return null if no user is logged in
  if (!user) {
    return null;
  }
  return (
  <div className="min-h-screen bg-gray-50 flex justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div className="w-full max-w-2xl">
      {/* Navigation buttons */}
      <div className="flex justify-end mb-6 space-x-4">
        <button
          onClick={handleBack}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back
        </button>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Home
        </button>
      </div>
      {/* Card container */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-blue-600 text-white p-5">
          <h1 className="text-xl font-bold">Upload Profile Photo</h1>
        </div>

        <div className="p-6 space-y-6">
          {/* Error message */}
          {error && (
            <div className="flex items-center p-4 bg-red-50 text-red-700 rounded-lg shadow-sm">
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </div>
          )}

          {/* Success message */}
          {success && (
            <div className="flex items-center p-4 bg-green-50 text-green-700 rounded-lg shadow-sm">
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              {success}
            </div>
          )}

          {/* File upload form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label htmlFor="photo" className="block text-sm text-gray-700 mb-1">
                Select Photo
              </label>
              <input
                id="photo"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-4">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Upload Photo
              </button>
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Back to Profile
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
);
}
// Export the UploadPhotoPage component for use in routes
export default UploadPhotoPage;
